import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { isAxiosError } from 'axios';
import { initiateStepUp, verifyStepUp } from '../services/auth.service';
import type { StepUpChallenge } from '../services/auth.service';
import { extractServerErrors } from '@/lib/api/error';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

const codeSchema = z.object({
  code: z.string().min(1, 'Code is required'),
});
type CodeValues = z.infer<typeof codeSchema>;

interface StepUpModalProps {
  open: boolean;
  title?: string;
  description?: string;
  onComplete: (stepUpToken: string) => void;
  onCancel: () => void;
}

const METHOD_PROMPT: Record<StepUpChallenge['method'], string> = {
  Totp: 'Enter the 6-digit code from your authenticator app.',
  EmailOtp: 'Enter the code sent to your email.',
  SmsOtp: 'Enter the code sent via SMS.',
};

export function StepUpModalComponent({
  open,
  title = 'Confirm your identity',
  description = 'For your security, please re-verify before continuing.',
  onComplete,
  onCancel,
}: StepUpModalProps) {
  const form = useForm<CodeValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  });

  const initiate = useMutation({
    mutationFn: () => initiateStepUp(),
  });

  const verify = useMutation({
    mutationFn: ({ challengeId, code }: { challengeId: string; code: string }) =>
      verifyStepUp(challengeId, code),
  });

  useEffect(() => {
    if (open) {
      form.reset({ code: '' });
      initiate.reset();
      verify.reset();
      initiate.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function onSubmit(values: CodeValues) {
    if (!initiate.data) return;
    try {
      const result = await verify.mutateAsync({
        challengeId: initiate.data.challengeId,
        code: values.code,
      });
      onComplete(result.stepUpToken);
    } catch (err) {
      if (isAxiosError(err) && err.response) {
        const msgs = extractServerErrors(err);
        form.setError('code', { message: msgs[0] ?? 'Invalid code. Please try again.' });
      } else {
        form.setError('code', { message: 'Network error. Please try again.' });
      }
    }
  }

  function handleOpenChange(next: boolean) {
    if (!next) onCancel();
  }

  const isPending = initiate.isPending || verify.isPending;
  const initError =
    initiate.isError && isAxiosError(initiate.error)
      ? extractServerErrors(initiate.error)[0] ?? 'Unable to start verification.'
      : initiate.isError
        ? 'Unable to start verification. Please try again.'
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-muted-foreground" />
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {initiate.isPending && (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}

        {initError && (
          <div className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{initError}</span>
          </div>
        )}

        {initiate.data && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {METHOD_PROMPT[initiate.data.method]}
              </p>
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Verification code</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="one-time-code"
                        autoFocus
                        inputMode={initiate.data?.method === 'Totp' ? 'numeric' : undefined}
                        maxLength={initiate.data?.method === 'Totp' ? 6 : undefined}
                        placeholder={initiate.data?.method === 'Totp' ? '000000' : ''}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {verify.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {verify.isPending ? 'Verifying…' : 'Verify'}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
